import { useEffect, useRef, useState } from "react"

const THRESHOLD_PX = 6

// Dragging a tournament of the Season view with the mouse (a keyboard moves it from its quick peek, ticket 15): a press becomes a drag
// after 6 px, follows the cell the pointer enters, and drops when the button is released. The click that ends a drag is not a click.
// `press(item, origin)` (an origin, or a function of the press), `enter(target)` and `click(handler)` give the handlers; `drag` is
// { item, origin, target } while it lasts.
export default function useChipDrag(onDrop) {
  const [drag, setDrag] = useState(null)
  const pressed = useRef(null)
  const swallowClick = useRef(false)
  const dropped = useRef(onDrop)
  dropped.current = onDrop
  useEffect(() => {
    const move = event => {
      const current = pressed.current
      if (!current || current.active || Math.abs(event.clientX - current.x) + Math.abs(event.clientY - current.y) < THRESHOLD_PX) return
      current.active = true
      setDrag({ item: current.item, origin: current.origin, target: current.target })
    }
    const release = () => {
      const current = pressed.current
      pressed.current = null
      if (!current?.active) return
      swallowClick.current = true
      setDrag(null)
      if (current.target) dropped.current(current.item, current.origin, current.target)
    }
    document.addEventListener("mousemove", move)
    document.addEventListener("mouseup", release)
    return () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", release) }
  }, [])
  const press = (item, origin) => event => {
    if (event.button !== 0) return
    // No text selection while dragging.
    event.preventDefault()
    swallowClick.current = false
    pressed.current = { item, origin: typeof origin === "function" ? origin(event) : origin, x: event.clientX, y: event.clientY, active: false, target: null }
  }
  // The cell entered while pressed, even before the drag starts: on a quick drag the browser enters the target before the move that
  // passes the threshold (localhost check, 2026-10-21).
  const enter = target => () => {
    const current = pressed.current
    if (!current) return
    current.target = target
    if (current.active) setDrag(previous => previous && { ...previous, target })
  }
  // A keyboard's click (detail 0) is always a click.
  const click = handler => event => { if (event.detail === 0 || !swallowClick.current) handler(event) }
  return { drag, press, enter, click }
}
